// Helpers for reading the current user's roles from localStorage.
//
// Multi-role model:
//   - "roles"  : JSON array of every role the user holds (authoritative list)
//   - "role"   : legacy single primary role (backward-compatible fallback)
//   - "activeRole" : the role the user is currently "acting as". When set to a
//                    role the user actually holds, the whole UI narrows its
//                    permissions to that single role. This lets a user who is
//                    e.g. super_admin + moderator + faculty switch context and
//                    have the site behave as that role.

const ACTIVE_ROLE_KEY = "activeRole";

// Privilege ranking (highest first). Used to pick a sensible default active
// role for multi-role users: e.g. someone who is super_admin + hr + faculty
// defaults to super_admin and gets full access until they switch roles.
const ROLE_PRIORITY = [
  "super_admin",
  "admin",
  "hr",
  "hod",
  "auditor",
  "moderator",
  "faculty",
  "general",
];

// Pick the highest-privilege role out of a list the user holds. Unknown/custom
// roles rank below all known roles but above nothing (kept, not dropped).
export const getHighestRole = (roles) => {
  const list = Array.isArray(roles) ? roles.filter(Boolean) : [];
  if (!list.length) return "general";
  const rank = (r) => {
    const i = ROLE_PRIORITY.indexOf(r);
    return i === -1 ? ROLE_PRIORITY.length : i;
  };
  return [...list].sort((a, b) => rank(a) - rank(b))[0];
};

// Full list of every role the user holds.
export const getAllRoles = () => {
  try {
    const raw = localStorage.getItem("roles");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch (e) {
    // ignore malformed value
  }
  const single = localStorage.getItem("role");
  return single ? [single] : [];
};

// The user's primary role (from login), used as the default active role.
export const getPrimaryRole = () => {
  const single = localStorage.getItem("role");
  if (single) return single;
  const all = getAllRoles();
  return all[0] || "general";
};

// The role the user is currently acting as. Falls back to the HIGHEST-privilege
// role the user holds when unset or no longer valid, so multi-role users start
// with their most capable role by default.
export const getActiveRole = () => {
  const all = getAllRoles();
  const stored = localStorage.getItem(ACTIVE_ROLE_KEY);
  if (stored && all.includes(stored)) return stored;
  return getHighestRole(all);
};

// Persist the active role (only if the user actually holds it).
export const setActiveRole = (role) => {
  const all = getAllRoles();
  if (all.includes(role)) {
    localStorage.setItem(ACTIVE_ROLE_KEY, role);
    return true;
  }
  return false;
};

export const clearActiveRole = () => localStorage.removeItem(ACTIVE_ROLE_KEY);

// Effective roles = just the active role. All UI gating flows through this so
// switching roles reactively changes what the site exposes.
export const getRoles = () => {
  const active = getActiveRole();
  return active ? [active] : [];
};

// True if the user's ACTIVE role is any of the given roles.
export const hasAnyRole = (...roleList) => {
  const active = getActiveRole();
  return roleList.includes(active);
};

// True if the user holds the given role at all (ignores the active role).
// Useful for building the role switcher / knowing what a user could switch to.
export const holdsRole = (...roleList) => {
  const all = getAllRoles();
  return all.some((r) => roleList.includes(r));
};

// Moderator powers apply only while acting as moderator (or super_admin, who
// is always treated as a moderator). A multi-role user acting as "faculty"
// should NOT retain moderator abilities.
export const isModerator = () => {
  const flagged = localStorage.getItem("is_moderator") === "true";
  const active = getActiveRole();
  if (active === "super_admin") return true;
  return flagged && active === "moderator";
};
